import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BASE_PATH } from '../../api';
import { AuthService } from './auth.service';
import { codificarParametro } from './asociados-listado.service';

/**
 * Estado derivado de una relación del histórico (modelo real): baja = tipo 3; vigente = relación del ejercicio vigente con active = 1;
 * historico = relación de un ejercicio cerrado (active = 0 es lo normal allí, no una baja); desactivado = anomalía en el ejercicio vigente.
 */
export type EstadoRelacion = 'vigente' | 'historico' | 'baja' | 'desactivado';

export interface RelacionHistorico {
  idAsociacion: number;
  nombreAsociacion: string;
  tipoAsociacion: number | null;
  idAsociado: number;
  nombre: string;
  apellidos: string;
  idCargo: number;
  cargo: string;
  esInfantil: number | null;
  idEjercicio: number;
  anyoEjercicio: number;
  /** 1 si es el ejercicio vigente. */
  ejercicioVigente: number;
  idTipoHistorico: number;
  /** Nombre del tipo según la base (Altas, Cambios, Bajas). */
  tipo: string | null;
  active: number;
  estado: EstadoRelacion;
  fechaBaja?: string | null;
}

export interface PaginacionHistorico { page: number; pageSize: number; total: number; totalPages: number; dir: 'asc' | 'desc'; }
export interface PaginaHistorico { historico: RelacionHistorico[]; paginacion: PaginacionHistorico; }

export interface ConsultaHistorico {
  asociado?: number | null;
  ejercicio?: number | null;
  asociacion?: number | null;
  cargo?: number | null;
  tipo?: number | null;
  estado?: EstadoRelacion | 'todos' | null;
  page: number;
  pageSize: number;
  dir?: 'asc' | 'desc';
}

/** Clave lógica de un registro del histórico. */
export interface ClaveHistorico { idAsociacion: number; idAsociado: number; idCargo: number; ejercicio: number; }

export interface CambiosHistorico {
  idAsociacion?: number;
  idCargo?: number;
  ejercicio?: number;
  idTipoHistorico?: number;
  /** Estado que se está editando (control de concurrencia: si cambió, la API responde 409). */
  esperado: { active: number; idTipoHistorico: number };
}

/** crear = fila nueva; actualizar = la relación consta en baja y se actualiza en el sitio (reingreso o cargo recuperado); creada/actualizada = ya aplicadas. */
export type EstadoFilaLote = 'crear' | 'actualizar' | 'existente' | 'conflicto' | 'creada' | 'actualizada';
/** Movimiento administrativo que se registra: ALTA (no pertenecía a la asociación en ese ejercicio) o CAMBIO (ya pertenecía con otro cargo). */
export type MovimientoLote = 'alta' | 'cambio';

export interface FilaLote {
  idEjercicio: number;
  anyoEjercicio: number;
  ejercicioVigente: number;
  estado: EstadoFilaLote;
  motivo: string | null;
  mensaje: string | null;
  tipoMovimiento?: MovimientoLote | null;
  /** Tipo que se escribe en el histórico (1 Altas, 2 Cambios). */
  idTipoHistorico?: number | null;
  activa?: number | null;
  /** Estado previo de la fila que se actualiza (la baja anterior): también queda en la auditoría. */
  antes?: { idTipoHistorico: number; active: number; fechaBaja: string | null } | null;
}

export interface EntradaLote { idAsociado: number; idAsociacion: number; idCargo: number; ejercicios: number[]; }

export interface PlanLote {
  asociado: { id: number; nombre: string; apellidos: string };
  asociacion: { id: number; nombre: string };
  cargo: { id: number; nombre: string };
  filas: FilaLote[];
  avisos: string[];
  resumen: { crear?: number; actualizar?: number; creadas?: number; actualizadas?: number; altas?: number; cambios?: number; existentes: number; conflictos: number };
  /** Solo en la vista previa: liga la confirmación a los datos y al estado de la base. */
  firma?: string;
}

/**
 * Histórico de cargos (P1.3 Wave 5): consulta paginada, edición de un registro y asignación a varios ejercicios (vista previa +
 * aplicación). La autorización la valida la API (permiso global historico:escribir para las escrituras).
 */
@Injectable({ providedIn: 'root' })
export class HistoricoApiService {
  constructor(
    private readonly http: HttpClient,
    private readonly auth: AuthService,
    @Inject(BASE_PATH) private readonly basePath: string
  ) {}

  private cabeceras(): HttpHeaders {
    return new HttpHeaders({ Authorization: 'Bearer ' + this.auth.getToken() });
  }

  construirUrl(consulta: ConsultaHistorico): string {
    const partes: [string, string][] = [['page', String(consulta.page)], ['pageSize', String(consulta.pageSize)]];
    for (const [nombre, valor] of [['asociado', consulta.asociado], ['ejercicio', consulta.ejercicio], ['asociacion', consulta.asociacion], ['cargo', consulta.cargo], ['tipo', consulta.tipo]] as [string, number | null | undefined][]) {
      if (valor) partes.push([nombre, String(valor)]);
    }
    if (consulta.estado && consulta.estado !== 'todos') partes.push(['estado', consulta.estado]);
    if (consulta.dir) partes.push(['dir', consulta.dir]);
    return `${this.basePath}/historico?` + partes.map(([k, v]) => `${k}=${codificarParametro(v)}`).join('&');
  }

  consultar(consulta: ConsultaHistorico): Observable<PaginaHistorico> {
    return this.http.get<PaginaHistorico>(this.construirUrl(consulta), { headers: this.cabeceras() });
  }

  private urlClave(clave: ClaveHistorico): string {
    return `${this.basePath}/historico/${clave.idAsociacion}/${clave.idAsociado}/${clave.idCargo}/${clave.ejercicio}`;
  }

  obtener(clave: ClaveHistorico): Observable<{ historico: RelacionHistorico[] }> {
    return this.http.get<{ historico: RelacionHistorico[] }>(this.urlClave(clave), { headers: this.cabeceras() });
  }

  editar(clave: ClaveHistorico, cambios: CambiosHistorico): Observable<{ historico: RelacionHistorico[] }> {
    return this.http.put<{ historico: RelacionHistorico[] }>(this.urlClave(clave), cambios, { headers: this.cabeceras() });
  }

  vistaPreviaLote(entrada: EntradaLote): Observable<{ lote: PlanLote }> {
    return this.http.post<{ lote: PlanLote }>(`${this.basePath}/historico/lote/vista-previa`, entrada, { headers: this.cabeceras() });
  }

  aplicarLote(entrada: EntradaLote, firma: string): Observable<{ lote: PlanLote }> {
    return this.http.post<{ lote: PlanLote }>(`${this.basePath}/historico/lote`, { ...entrada, firma }, { headers: this.cabeceras() });
  }
}
